import React, { useEffect, useState, useContext } from "react";
import styled, { ThemeProvider } from "styled-components";
import { Link } from "react-router-dom";
import { MobileDrawer } from "components/UI/MobileDrawer";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import { themeFromSiteConfig } from "components/Site/Contexts/SiteTheme";

import { AppContext } from "../App/AppProvider";
import { useAuthContext } from "../Auth/AuthContext";
import { ViewSettingsMenu } from "../UI/ViewSettingsMenu";
import { Theme } from "../../typedefs-ui/Theme";
import { FeedbackIcon } from "components/UI/Feedback";
import PeopleMan1Icon from "icons/PeopleMan1Icon";
import {
  useFeedbackRouting,
  useFeedbackStatus
} from "components/Feedback/utils";
import { SiteIconImage } from "components/Site/SiteImages";
import media from "utils/media";

const StyledSiteDrawer = styled(MobileDrawer)`
  z-index: 9999;
  transition: transform 150ms ease-in-out;
  transform: translateX(
    ${props => (props.anchor === "left" ? "-100%" : "100%")}
  );
  ${({ theme }) => theme.darkMode && `background: ${theme.computedPrimary};`}
  box-shadow: unset;

  &.open {
    transform: translateX(0);
    box-shadow: 2px 0px 6px rgba(0, 0, 0, 0.25);
  }

  ${media.desktop`display: none;`}
`;

const SiteDrawerOverlay = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 9998;
`;

interface SiteDrawerProps {
  theme: Theme;
}

const SiteDrawer: React.FC<SiteDrawerProps> = ({ theme }) => {
  const [settingsVisible, setSettingsVisiblity] = useState(false);
  const site = useSiteConfig();
  const { drawerIsOpen, toggleDrawer } = useContext(AppContext);
  const { isAuthenticated } = useAuthContext();
  const anchor = site.scriptDirection === "RTL" ? "right" : "left";
  const { dblId, bibleId } = useFeedbackRouting();
  const { enabled: feedbackEnabled } = useFeedbackStatus({ dblId });

  useEffect(() => {
    !drawerIsOpen && setSettingsVisiblity(false);
  }, [drawerIsOpen]);

  return (
    <ThemeProvider theme={{ ...themeFromSiteConfig(site), ...theme }}>
      <>
        {drawerIsOpen && <SiteDrawerOverlay onClick={toggleDrawer} />}
        <StyledSiteDrawer
          anchor={anchor}
          toggleDrawer={toggleDrawer}
          className={drawerIsOpen ? "open" : ""}
          icon={<SiteIconImage width={54} />}
        >
          {({ iconColor, InternalLink, Action }: any) => (
            <>
              {feedbackEnabled && (
                <Link to={`/bible/${bibleId}/recent-feedback`}>
                  <Action>
                    <FeedbackIcon size={24} color={iconColor} />
                  </Action>
                </Link>
              )}
              <Action onClick={() => setSettingsVisiblity(!settingsVisible)}>
                <ViewSettingsMenu
                  anchor={anchor}
                  iconColor={iconColor}
                  open={drawerIsOpen && settingsVisible}
                />
              </Action>
              {site.enabledUserAccounts && (
                <InternalLink
                  onClick={toggleDrawer}
                  to={isAuthenticated ? "/user-settings" : "/auth"}
                >
                  <PeopleMan1Icon size={24} color={iconColor} />
                </InternalLink>
              )}
            </>
          )}
        </StyledSiteDrawer>
      </>
    </ThemeProvider>
  );
};

export default SiteDrawer;
